import { inject, injectable } from "@launchtray/tsyringe-async";
import { SoundResourcesPackage } from "../../../common/resources/SoundResourcesPackage";
import { Sound } from "@pixi/sound";
import { SettingsModel } from "../../../common/models/SettingsModel";
import { nameof } from "bindable-data";
import { InjectableInterfaces } from "../../../composition/InjectableInterfaces";
import { IGameDistributionSdk } from "../../../gamedistribution/interfaces/IGameDistributionSdk";

export enum Music {
    LevelBackground = "Level",
    MenuBackground = "Menu"
}

export enum Sfx {
    CoinCollect,
    Eating,
    Electricity,
    Explosion,
    Freeze,
    Hit,
    PurchaseItem,
    TractorArrive,
    TurretShoot
}

@injectable()
export class AudioController {
    private static readonly MUSIC_KEYS = new Map<Music, keyof SoundResourcesPackage>([
        [Music.LevelBackground, "assets_sounds_music_level_mp3"],
        [Music.MenuBackground, "assets_sounds_music_menu_mp3"]
    ]);
    private static readonly SFX_KEYS = new Map<Sfx, keyof SoundResourcesPackage>([
        [Sfx.CoinCollect, "assets_sounds_CollectCoinSound_mp3"],
        [Sfx.Eating, "assets_sounds_CarrotEatingSound_mp3"],
        [Sfx.Electricity, "assets_sounds_ElectricitySound_mp3"],
        [Sfx.Explosion, "assets_sounds_ExplosionSound_mp3"],
        [Sfx.Freeze, "assets_sounds_FreezeSound_mp3"],
        [Sfx.Hit, "assets_sounds_HitSound_mp3"],
        [Sfx.PurchaseItem, "assets_sounds_BuyShopItemSound_mp3"],
        [Sfx.TractorArrive, "assets_sounds_TractorArrive_mp3"],
        [Sfx.TurretShoot, "assets_sounds_TurretShootSound_mp3"]
    ]);

    private playingMusic: [Music, Sound];

    private firstInteractionDone = false;
    private pendingMusic: Music | null = null;
    private isSkipAdAudioResume: boolean = false;

    public constructor(
        private readonly soundResources: SoundResourcesPackage,
        @inject(InjectableInterfaces.IGameDistributionSdk) private readonly gdSdk: IGameDistributionSdk,
        private readonly settingsModel: SettingsModel
    ) {
        const unlock = () => {
            this.firstInteractionDone = true;
            window.removeEventListener("click", unlock);
            window.removeEventListener("touchstart", unlock);

            if (this.pendingMusic) {
                this.playMusic(this.pendingMusic);
                this.pendingMusic = null;
            }
        };

        this.onLoseFocusInAd = this.onLoseFocusInAd.bind(this);
        window.addEventListener("click", unlock);
        window.addEventListener("touchstart", unlock);

        for (const [_, soundKey] of AudioController.MUSIC_KEYS) {
            const sound = this.soundResources[soundKey] as Sound;
            sound.muted = !settingsModel.musicEnabled.value;
            sound.volume = 0.25;
            sound.loop = true;
            sound.singleInstance = true;
        }
        this.gdSdk.pauseAudio.add(this.pauseAudioForAd, this);
        this.gdSdk.resumeAudio.add(this.resumeAudioForAd, this);
        this.settingsModel.propertyChanged.add(this.onSettingsModelPropertyChange, this);
        this.onSettingsModelPropertyChange(this.settingsModel, nameof(this.settingsModel, "sfxEnabled"));
    }

    public playMusic(music: Music): void {
        if (!AudioController.MUSIC_KEYS.has(music)) {
            throw new Error(`Unexpected music key - ${music}`);
        }
        // If user hasn't interacted yet, queue the music
        if (!this.firstInteractionDone) {
            this.pendingMusic = music;
            return;
        }

        if (this.isSkipAdAudioResume) {
            this.isSkipAdAudioResume = false;
            return;
        }

        if (this.playingMusic != null) {
            const [currentMusic, currentSound] = this.playingMusic;
            if (currentMusic === music) {
                if (currentSound.isPlaying) {
                    console.warn(`Music ${music} already playing!`);
                } else {
                    currentSound.play();
                }
                return;
            } else {
                currentSound.stop();
            }
        }

        // if (this.isSkipAdAudioResume) {
        //     this.isSkipAdAudioResume = false;
        //     setTimeout(() => {
        //         const soundKey = AudioController.MUSIC_KEYS.get(music);
        //         const sound = this.soundResources[soundKey] as Sound;
        //         this.playingMusic = [music, sound];
        //         sound.play();
        //     }, 150);
        //     return;
        // }

        const soundKey = AudioController.MUSIC_KEYS.get(music);
        const sound = this.soundResources[soundKey] as Sound;
        this.playingMusic = [music, sound];
        sound.play();
    }

    public stopMusic(): void {
        if (this.playingMusic == null) {
            return;
        }
        const [_, sound] = this.playingMusic;
        sound.stop();
    }

    public playSfx(sfx: Sfx): void {
        if (!AudioController.SFX_KEYS.has(sfx)) {
            console.error(`Unable to play sfx, unsupported sfx: ${sfx}`);
            return;
        }
        const soundKey = AudioController.SFX_KEYS.get(sfx);
        const sound = this.soundResources[soundKey] as Sound;
        sound.volume = 0.5;
        sound.play();
    }

    public getSfxLoopInstance(sfx: Sfx): Sound {
        if (!AudioController.SFX_KEYS.has(sfx)) {
            console.error(`Unable to play sfx, unsupported sfx: ${sfx}`);
            return;
        }
        const soundKey = AudioController.SFX_KEYS.get(sfx);
        const sound = this.soundResources[soundKey] as Sound;
        sound.volume = 0.5;
        sound.loop = true;
        return sound;
    }

    public pauseAudioForAd(): void {
        window.addEventListener("blur", this.onLoseFocusInAd);
        if (!this.playingMusic) {
            return;
        }
        this.playingMusic[1].pause();
    }

    public resumeAudioForAd(): void {
        window.removeEventListener("blur", this.onLoseFocusInAd);
        if (!this.playingMusic) {
            return;
        }
        this.playingMusic[1].resume();
    }

    private onSettingsModelPropertyChange(model: SettingsModel, key: string): void {
        switch (key) {
            case nameof(model, "musicEnabled"):
                for (const [_sfx, soundKey] of AudioController.MUSIC_KEYS) {
                    const sound = this.soundResources[soundKey] as Sound;
                    sound.muted = !model.musicEnabled.value;
                }
                break;
            case nameof(model, "sfxEnabled"):
                for (const [_sfx, soundKey] of AudioController.SFX_KEYS) {
                    const sound = this.soundResources[soundKey] as Sound;
                    sound.muted = !model.sfxEnabled.value;
                }
                break;
        }
    }

    private onLoseFocusInAd(): void {
        this.isSkipAdAudioResume = true;
    }
}
